import type { AxiosInstance } from 'axios';
import type { components, paths } from '../schema';
import type { Field } from '../types/field';
import type {
  PaginatedResponse,
  RequestOptions,
  RequestOptionsWithParams,
} from './types';
import { curry2, curry3 } from './utils';

export type FormServiceConfig = {
  EMBED_API_DOMAIN: string;
  TYPE?: FormType;
};

export type FormType = 'forms' | 'surveys';

type SubmitFormOptions = RequestOptions & {
  isPartial?: boolean;
  submissionId?: string | null;
  recaptchaToken?: string;
  isIframe?: boolean;
};

type FormSubmitPayload = {
  fields: any[];
  partial_submission: boolean;
  challenge_token?: string;
};

type FormPreviewResponse = {
  business_id: string;
  business_merge_field_data: Record<string, string>;
  challenged_token_required: boolean;
  fields: Field[];
  deleted_field_ids: string[];
  disabled_action: components['schemas']['DisabledActionEnum'] | null;
  disabled_target_form: string | null;
  disabled_target_url: string | null;
  enabled: boolean;
  form_ui: {
    pages: Array<{
      hidden: boolean;
      id: string;
      is_deletable: boolean;
      is_form_page: boolean;
      is_hideable: boolean;
      page_data: string;
      page_name: string;
    }>;
  };
  id: string;
  pass_variables_on_redirect: boolean;
  redirect_parameter_field_ids: string[];
  redirect_url: string;
  related_object: {
    entity_name: string;
    fetch_url: string;
    id: string;
    name: string;
    object_name: string;
    object_type: string;
  };
  submission_action: 'go_to_page' | 'go_to_url';
};

type FormSubmitResponse = {
  created: string;
  fields: Array<{
    display_name: string;
    field_type: string;
    id: string;
    name: string;
    value: string | null;
  }>;
  id: string;
  ip_address: string;
  partial_submission: boolean;
};

export type FormListItem = Mutable<
  components['schemas']['FormObjectSlimmedDetail']
>;

export type Form = Mutable<components['schemas']['FormObject']>;

export type GetFormsParams = Exclude<
  paths['/api/forms']['get']['parameters']['query'],
  undefined
>;

const getParentWindow = (window: any) => {
  if (window.parent instanceof Window) {
    return window.parent;
  }
  if (window?.parent?.[0] instanceof Window) {
    return window.parent[0];
  }
  return null;
};

const hasProtocol = (url: string) => {
  return url.startsWith('https://') || url.startsWith('http://');
};

export const getForm = async (
  instance: AxiosInstance,
  type: FormType,
  formId: string,
  options: RequestOptions = {}
): Promise<Form> => {
  const res = await instance.get(`/${type}/${formId}`, options);

  return res.data;
};

export const getForms = async (
  instance: AxiosInstance,
  type: FormType,
  options: RequestOptionsWithParams<GetFormsParams> = {}
): Promise<PaginatedResponse<FormListItem>> => {
  const res = await instance.get(`/${type}`, options);

  return res.data;
};

export const getFormPreview = async (
  instance: AxiosInstance,
  type: FormType,
  baseUrl: string,
  id: string,
  options?: RequestOptions
): Promise<FormPreviewResponse> => {
  const base = hasProtocol(baseUrl) ? baseUrl : `https://${baseUrl}`;

  const res = await instance.get<FormPreviewResponse>(
    `${base}/${type}/${id}/page-view`,
    options
  );

  return res.data;
};

export const submitForm = async (
  instance: AxiosInstance,
  type: FormType,
  baseUrl: string,
  id: string,
  fields: any[],
  options: SubmitFormOptions = {}
): Promise<FormSubmitResponse> => {
  const {
    isPartial = false,
    submissionId = null,
    recaptchaToken,
    isIframe = false,
    ...config
  } = options;
  const base = hasProtocol(baseUrl) ? baseUrl : `https://${baseUrl}`;
  const page_params = new URLSearchParams(window.location.search);
  const req_params = new URLSearchParams();
  const payload: FormSubmitPayload = {
    fields,
    partial_submission: isPartial,
    ...(recaptchaToken && { challenge_token: recaptchaToken }),
  };

  if (page_params.has('session_id')) {
    req_params.append('session_id', page_params.get('session_id')!);
  } else if (isIframe) {
    const parentWindow = getParentWindow(window);
    if (parentWindow) {
      req_params.append('referrer', parentWindow.location.href);
    }
  } else if (document.referrer) {
    req_params.append('referrer', document.referrer);
  }

  const qs = req_params.toString().length ? `?${req_params.toString()}` : '';
  const submit_url = `${base}/${type}/${id}/submit`;

  if (submissionId) {
    const res = await instance.patch<FormSubmitResponse>(
      `${submit_url}/${submissionId}${qs}`,
      payload,
      config
    );

    return res.data;
  }

  const res = await instance.post(submit_url + qs, payload, config);

  return res.data;
};

export class FormService {
  public getForm;
  public getForms;
  public getFormPreview;
  public submitForm;

  constructor(
    private instance: AxiosInstance,
    config: FormServiceConfig
  ) {
    const type = config?.TYPE || 'forms';
    this.getForm = curry2(getForm)(this.instance, type);
    this.getForms = curry2(getForms)(this.instance, type);
    this.getFormPreview = curry3(getFormPreview)(
      this.instance,
      type,
      config.EMBED_API_DOMAIN
    );
    this.submitForm = curry3(submitForm)(
      this.instance,
      type,
      config.EMBED_API_DOMAIN
    );
  }
}
